import React, { useState, useEffect } from 'react';
import { leadQuestions } from '../data/leadQuestions';
import Question from './Question';
import { useGlobal } from 'context';
import { withLayout } from 'helpers';
import useQuery from 'utils/hooks/useQuery';
import { request } from 'utils/api';
import { PUBLIC_TOKEN } from 'utils/env';
import { useTracker } from 'utils/tracking';

const QuestionComponent = withLayout(Question);

export default function Questionnaire({
  history,
  prevRoute,
  nextRoute,
  disqualifyingRoute,
}) {
  const context = useGlobal();
  const query = useQuery();
  const trackOnce = useTracker();

  const [answers, setAnswers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({});

  useEffect(() => {
    if (!context?.leadQuestions.length) {
      setCurrentQuestion(leadQuestions[0]);
    } else {
      let question = leadQuestions.find(
        (q) =>
          q.id === context.leadQuestions[context.leadQuestions.length - 1].id
      );
      setCurrentQuestion(question);
    }
    setAnswers(context.leadQuestions || []);

    async function fetchLeadData() {
      const leadId = query.get('rId');
      const code = query.get('code');
      if (!leadId || !code) return;

      let data;
      try {
        data = await request({
          method: 'GET',
          path: `/v1/web/auth/resume/${leadId}`,
          params: { code },
          headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
        });
      } catch (error) {
        // Redirect to start if error
        history.push('/talk-to-us');
      }

      if (!data?.content) return;

      // Skip until red flags question
      setCurrentQuestion(leadQuestions[4]);

      const content = data.content;

      context.setStartInfo({
        ...context.startInfo,
        dateOfBirth: content.dateOfBirth,
        state: content.state,
        entheaCompany: content.entheaCompany,
      });

      context.setAccount({
        ...context.account,
        firstName: content.firstName,
        lastName: content.lastName,
        email: content.email,
      });
    }

    fetchLeadData();
  }, []);

  function previousStep() {
    if (leadQuestions.indexOf(currentQuestion) > 0) {
      setCurrentQuestion(
        leadQuestions[leadQuestions.indexOf(currentQuestion) - 1]
      );
    }
    if (leadQuestions.indexOf(currentQuestion) === 0) {
      history.push(prevRoute);
    }
  }

  function nextStep() {
    trackOnce(currentQuestion.gaTag);

    const currentIndex = leadQuestions.indexOf(currentQuestion);
    if (currentIndex < leadQuestions.length - 1) {
      setCurrentQuestion(leadQuestions[currentIndex + 1]);
    }

    if (currentIndex !== leadQuestions.length - 1) {
      return;
    }

    if (
      !sessionStorage.getItem('flow')?.includes('checkout') ||
      !currentQuestion.checkoutIfPositive
    ) {
      history.push(nextRoute);
      return;
    }

    let lastAnswer = answers.find((a) => a.id === currentQuestion.id) || {};
    if (lastAnswer.answer.some((e) => e !== currentQuestion.positiveValue)) {
      // Go to schedule call if there are red flags
      history.push(nextRoute);
      return;
    }

    if (!sessionStorage.plan) {
      history.push(nextRoute);
      return;
    }

    history.push(`/checkout/${sessionStorage.plan}/account`);
  }

  function disqualified() {
    history.push(disqualifyingRoute);
  }

  if (currentQuestion.type === 'custom') {
    const StepComponent = currentQuestion.content;
    return <StepComponent onBack={previousStep} onNext={nextStep} />;
  }

  return (
    <QuestionComponent
      question={currentQuestion}
      answers={answers}
      setAnswers={setAnswers}
      onBack={previousStep}
      onNext={nextStep}
      onDisqualified={disqualified}
      meta={{
        title: `Question ${currentQuestion.stepNumber} | Nue Life Questionnaire`,
      }}
    />
  );
}